import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import SEOHelmet from './SEOHelmet';
import './ReviewsPage.css';

const REVIEWS_PER_PAGE = 8;

const formatReviewDate = (date) => {
  if (!date) return 'Shared by a recent client';

  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const ReviewsPage = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showReviewSuccess, setShowReviewSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const storiesRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchReviews = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = await fetch(
          `https://tophamtechbe.com/api/treas/reviews/v1/get-review?site_id=${SITE_CONFIG.SITE_ID}`,
          {
            headers: { 'X-API-Key': 'treas-api-key-2025' },
            signal: controller.signal
          }
        );

        if (!response.ok) throw new Error(`Review request failed with ${response.status}`);

        const data = await response.json();
        const sortedReviews = (Array.isArray(data) ? data : [])
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

        setReviews(sortedReviews);
        setCurrentPage(1);
      } catch (fetchError) {
        if (fetchError.name !== 'AbortError') {
          console.error('Error fetching reviews:', fetchError);
          setError(true);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchReviews();
    return () => controller.abort();
  }, [reloadKey]);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, []);

  useEffect(() => {
    if (!showCreateForm && !showReviewSuccess) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const closeModal = (event) => {
      if (event.key !== 'Escape') return;
      setShowCreateForm(false);
      setShowReviewSuccess(false);
    };

    document.addEventListener('keydown', closeModal);
    return () => {
      document.removeEventListener('keydown', closeModal);
      document.body.style.overflow = previousOverflow;
    };
  }, [showCreateForm, showReviewSuccess]);

  const pageCount = Math.max(1, Math.ceil(reviews.length / REVIEWS_PER_PAGE));
  const pageStart = (currentPage - 1) * REVIEWS_PER_PAGE;
  const paginatedReviews = useMemo(
    () => reviews.slice(pageStart, pageStart + REVIEWS_PER_PAGE),
    [reviews, pageStart]
  );

  const changePage = (nextPage) => {
    setCurrentPage(nextPage);
    storiesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmitReview = async (event) => {
    event.preventDefault();
    const submittedReview = reviewText.trim();
    if (!submittedReview) return;

    setSubmitting(true);

    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/reviews/v1/store-review', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': 'treas-api-key-2025'
        },
        body: JSON.stringify({
          site_id: SITE_CONFIG.SITE_ID,
          review_text: submittedReview
        })
      });

      if (!response.ok) throw new Error(`Review submission failed with ${response.status}`);

      try {
        await navigator.clipboard?.writeText(submittedReview);
      } catch (clipboardError) {
        console.warn('Review submitted, but clipboard access was unavailable:', clipboardError);
      }

      setReviewText('');
      setShowCreateForm(false);
      setShowReviewSuccess(true);
      setReloadKey((key) => key + 1);
    } catch (submitError) {
      console.error('Error submitting review:', submitError);
      alert('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`reviews-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      <SEOHelmet
        title={`Client Stories & Reviews | ${SITE_CONFIG.AGENT.NAME}`}
        description={`Read client stories about working with ${SITE_CONFIG.AGENT.NAME} to buy and sell homes throughout Metro Atlanta.`}
        keywords="Atlanta real estate reviews, client testimonials, Atlanta realtor reviews, home buying experiences"
        canonical={`https://${SITE_CONFIG.BASE_URL}/reviews`}
      />

      <header className="reviews-site-header">
        <button className="reviews-brand" type="button" onClick={() => navigate('/')} aria-label="Return home">
          <span className="reviews-brand-mark" aria-hidden="true">
            {SITE_CONFIG.AGENT.NAME.split(' ').map((word) => word[0]).join('')}
          </span>
          <span className="reviews-brand-copy">
            <strong>{SITE_CONFIG.AGENT.NAME}</strong>
            <span>Real Estate Group</span>
          </span>
        </button>

        <nav className="reviews-nav" aria-label="Client stories navigation">
          <button type="button" onClick={() => navigate('/search')}>Search Homes</button>
          <button type="button" onClick={toggleTheme} aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} theme`}>
            {isDarkMode ? 'Light' : 'Dark'} Mode
          </button>
          <button className="reviews-home-button" type="button" onClick={() => navigate('/')}>Home <span aria-hidden="true">↗</span></button>
        </nav>
      </header>

      <main>
        <section className="reviews-hero" aria-labelledby="reviews-title">
          <div className="reviews-hero-glow" aria-hidden="true"></div>
          <div className="reviews-hero-content">
            <span className="reviews-eyebrow">Client Stories · Metro Atlanta</span>
            <h1 id="reviews-title">The best moves<br /><em>feel personal.</em></h1>
            <p>Every closing begins with trust. Read what local buyers and sellers have shared about the care, clarity, and confidence behind their experience.</p>
            <button className="reviews-primary-cta" type="button" onClick={() => setShowCreateForm(true)}>
              Share Your Story <span aria-hidden="true">→</span>
            </button>
          </div>

          <aside className="reviews-hero-profile" aria-label={`${SITE_CONFIG.AGENT.NAME} client service`}>
            <div className="reviews-profile-image">
              <img src={SITE_CONFIG.LANDING_PAGE.AGENT_IMAGE} alt={SITE_CONFIG.AGENT.NAME} decoding="async" fetchPriority="high" />
            </div>
            <div className="reviews-profile-copy">
              <span className="reviews-stars" aria-label="Five star service">★★★★★</span>
              <blockquote>“Local guidance, honest advice, and service that stays personal.”</blockquote>
              <p>{SITE_CONFIG.AGENT.NAME} · Atlanta, Georgia</p>
            </div>
          </aside>
          <span className="reviews-hero-wordmark" aria-hidden="true">Stories</span>
        </section>

        <section className="reviews-stories" ref={storiesRef} aria-labelledby="client-stories-title" aria-busy={loading}>
          <div className="reviews-section-heading">
            <div>
              <span>In their own words</span>
              <h2 id="client-stories-title">Client experiences</h2>
            </div>
            {!loading && reviews.length > 0 && (
              <p>Showing {pageStart + 1}–{Math.min(pageStart + REVIEWS_PER_PAGE, reviews.length)} of {reviews.length}</p>
            )}
          </div>

          {loading && (
            <div className="reviews-grid" aria-label="Loading client stories">
              {Array.from({ length: REVIEWS_PER_PAGE }).map((_, index) => (
                <div className={`review-card review-card--skeleton ${index === 0 ? 'review-card--featured' : ''}`} key={index}>
                  <span></span><span></span><span></span><span></span>
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="reviews-state-message" role="alert">
              <span>Connection interrupted</span>
              <h2>We couldn’t load the client stories.</h2>
              <p>Please try again and we’ll reconnect you.</p>
              <button type="button" onClick={() => setReloadKey((key) => key + 1)}>Try Again</button>
            </div>
          )}

          {!loading && !error && reviews.length === 0 && (
            <div className="reviews-state-message">
              <span>Be the first</span>
              <h2>Your story belongs here.</h2>
              <p>Share your experience working with {SITE_CONFIG.AGENT.NAME}.</p>
              <button type="button" onClick={() => setShowCreateForm(true)}>Write a Review</button>
            </div>
          )}

          {!loading && !error && paginatedReviews.length > 0 && (
            <div className="reviews-grid">
              {paginatedReviews.map((review, index) => (
                <article className={`review-card ${index === 0 ? 'review-card--featured' : ''}`} key={review.id || `${review.created_at}-${index}`}>
                  <div className="review-card-top">
                    <span className="review-card-stars" aria-label="Five stars">★★★★★</span>
                    <span className="review-card-number" aria-hidden="true">{String(pageStart + index + 1).padStart(2, '0')}</span>
                  </div>
                  <blockquote>{review.review_text}</blockquote>
                  <footer>
                    <span>Client Story</span>
                    <time dateTime={review.created_at}>{formatReviewDate(review.created_at)}</time>
                  </footer>
                </article>
              ))}
            </div>
          )}

          {!loading && !error && pageCount > 1 && (
            <nav className="reviews-pagination" aria-label="Client story pages">
              <button type="button" disabled={currentPage === 1} onClick={() => changePage(currentPage - 1)}>
                <span aria-hidden="true">←</span> Newer
              </button>
              <span aria-live="polite"><strong>{String(currentPage).padStart(2, '0')}</strong> / {String(pageCount).padStart(2, '0')}</span>
              <button type="button" disabled={currentPage === pageCount} onClick={() => changePage(currentPage + 1)}>
                Older <span aria-hidden="true">→</span>
              </button>
            </nav>
          )}
        </section>
      </main>

      <footer className="reviews-footer">
        <div>
          <span>Your next chapter starts here</span>
          <h2>Experience real estate that puts you first.</h2>
        </div>
        <button type="button" onClick={() => navigate('/contact-us')}>Work With {SITE_CONFIG.AGENT.NAME.split(' ')[0]} <span aria-hidden="true">→</span></button>
      </footer>

      {showCreateForm && (
        <div className="reviews-modal-overlay" onMouseDown={() => setShowCreateForm(false)}>
          <div className="review-modal" role="dialog" aria-modal="true" aria-labelledby="write-review-title" onMouseDown={(event) => event.stopPropagation()}>
            <button className="review-modal-close" type="button" onClick={() => setShowCreateForm(false)} aria-label="Close review form">×</button>
            <span className="review-modal-eyebrow">Share your experience</span>
            <h2 id="write-review-title">Write a review</h2>
            <p className="anonymous-note">Reviews are anonymous. Include your name in the message only if you would like it to appear.</p>
            <form onSubmit={handleSubmitReview}>
              <label htmlFor="review-message">Your story</label>
              <textarea
                id="review-message"
                placeholder="Tell us about your experience..."
                value={reviewText}
                onChange={(event) => setReviewText(event.target.value)}
                rows="7"
                maxLength="3000"
                autoFocus
                required
              />
              <div className="review-character-count">{reviewText.length} / 3000</div>
              <div className="review-modal-buttons">
                <button type="button" onClick={() => setShowCreateForm(false)}>Cancel</button>
                <button type="submit" disabled={submitting || !reviewText.trim()}>
                  {submitting ? 'Submitting…' : 'Submit Review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showReviewSuccess && (
        <div className="reviews-modal-overlay" onMouseDown={() => setShowReviewSuccess(false)}>
          <div className="review-success-modal" role="dialog" aria-modal="true" aria-labelledby="review-success-title" onMouseDown={(event) => event.stopPropagation()}>
            <span className="review-success-mark" aria-hidden="true">✓</span>
            <span className="review-modal-eyebrow">Thank you</span>
            <h2 id="review-success-title">Your story was submitted.</h2>
            <p>We’ve copied your review when clipboard access was available. You can also share it on your preferred review platform.</p>
            <div className="review-platform-buttons">
              <button type="button" onClick={() => window.open(SITE_CONFIG.AGENT.ZILLOW_REVIEW, '_blank', 'noopener,noreferrer')}>Continue to Zillow <span aria-hidden="true">↗</span></button>
              <button type="button" onClick={() => window.open(SITE_CONFIG.AGENT.GOOGLE_REVIEW, '_blank', 'noopener,noreferrer')}>Continue to Google <span aria-hidden="true">↗</span></button>
            </div>
            <button className="review-success-close" type="button" onClick={() => setShowReviewSuccess(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsPage;
